import { useState, useEffect, useCallback } from "react";
import { useLocation } from "react-router-dom";
import { fetchTemplateDetails, saveTemplateChanges } from "../../apis/templateApi/baseTemplateView"; // Assuming path is correct

// --- Interfaces remain the same ---

export interface TemplateDetail {
  key: string;
  value: string;
  env?: string;
  // Add flag for deletion tracking
  flag?: 'delete';
}

export interface TemplateDetails {
  Id: number;
  Config_Cd: string;
  Config_Url: string;
  Config_Nm: string;
  ConfigService_Typ: string;
}

export interface ServiceConfiguration {
  id: number;
  templateDetails: TemplateDetails | null;
  clientInfo: TemplateDetail[]; // Original data
  configurations: TemplateDetail[]; // Original data
  loading: boolean;
  error: string | null;
}

// Interface for representing calculated changes for a service
interface ServiceChanges {
    serviceId: number;
    changedClientInfo: TemplateDetail[];
    changedConfigurations: TemplateDetail[];
}

// Interface for formatted API data (structure remains the same, content changes)
interface FormattedApiData {
  [configCode: string]: TemplateDetail[]; // Will contain only changed/deleted items
}

// Helper to compare two detail arrays and find differences
const findDifferences = (original: TemplateDetail[], edited: TemplateDetail[]): TemplateDetail[] => {
    const changes: TemplateDetail[] = [];
    const originalMap = new Map(original.map(item => [item.key, item]));
    const editedMap = new Map(edited.map(item => [item.key, item]));

    // Check for updated and added items
    edited.forEach(editedItem => {
        const originalItem = originalMap.get(editedItem.key);
        if (!originalItem) {
            // Added item
            changes.push({ ...editedItem }); // Add a copy
        } else if (originalItem.value !== editedItem.value || originalItem.env !== editedItem.env) {
            // Modified item
            changes.push({ ...editedItem }); // Add a copy of the edited item
        }
    });

    // Check for deleted items
    original.forEach(originalItem => {
        if (!editedMap.has(originalItem.key)) {
            // Deleted item
            changes.push({ ...originalItem, flag: 'delete' }); // Mark for deletion
        }
    });

    return changes;
};


export const useServiceConfiguration = () => {
  const location = useLocation();
  const [serviceConfigurations, setServiceConfigurations] = useState<ServiceConfiguration[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);

  // --- useEffect for fetching data remains largely the same ---
  useEffect(() => {
    const fetchServiceConfigurations = async () => {
      setIsLoading(true);
      setError(null);
      setServiceConfigurations([]); // Clear previous results

      const path = location.pathname;
      const pathSegments = path.split("/");
      const serviceIdsSegment = pathSegments[pathSegments.length - 1];

      const serviceIds = serviceIdsSegment
      .split(",")
      .map((id) => {
        const decodedId = decodeURIComponent(id);
        return parseInt(decodedId.trim(), 10);
      })
      .filter((id) => !isNaN(id));

      console.log(serviceIdsSegment, serviceIds)

      if (serviceIds.length === 0) {
        setError("No valid service IDs found in URL");
        setIsLoading(false);
        return;
      }

      const configPromises = serviceIds.map(async (serviceId) => {
        try {
          // Simulate API call structure - replace with your actual fetch
          // const response = await fetchTemplateDetails(serviceId);
           const response = await fetchTemplateDetails(serviceId); // Use your actual API call

          if (response.success && response.data) {
            const { templateDetails, templateContent } = response.data;
            const serviceType = templateDetails.Config_Nm
            // Ensure data exists and provide defaults
            const clientInfo = templateContent?.client_info?.data?.map((d: any) => ({ ...d })) || [];
            let configurations = [];

            if(serviceType==='base') {
              configurations=templateContent?.configurations?.data?.map((d: any) => ({ ...d })) || []
            } else {
              console.log(serviceType)
              configurations = templateContent[0]?.[`${serviceType}`]?.data?.map((d: any) => ({ ...d })) || []
            }

            return {
              id: serviceId,
              templateDetails,
              clientInfo, // Store original data
              configurations, // Store original data
              loading: false,
              error: null
            };
          } else {
             console.error(`Failed fetch for service ID ${serviceId}:`, response.message);
            return {
              id: serviceId,
              templateDetails: null,
              clientInfo: [],
              configurations: [],
              loading: false,
              error: response.message || `Failed to fetch config for service ID ${serviceId}`
            };
          }
        } catch (error: any) {
           console.error(`Error fetching service ID ${serviceId}:`, error);
          return {
            id: serviceId,
            templateDetails: null,
            clientInfo: [],
            configurations: [],
            loading: false,
            error: `Error fetching service ID ${serviceId}: ${error.message || 'Unknown error'}`
          };
        }
      });

      try {
        const results = await Promise.all(configPromises);
        setServiceConfigurations(results);
      } catch (err: any) {
         console.error("Error processing configuration promises:", err);
        setError(`Failed to process service configurations: ${err.message || 'Unknown error'}`);
      } finally {
        setIsLoading(false);
      }
    };

    fetchServiceConfigurations();
  }, [location.pathname]); // Dependency on pathname ensures re-fetch on URL change

  // Function to format ONLY the differences for the API
  const formatChangesForApi = (allChanges: ServiceChanges[]): FormattedApiData => {
    const formattedData: FormattedApiData = {};

    allChanges.forEach(change => {
        const service = serviceConfigurations.find(sc => sc.id === change.serviceId);
        // Only proceed if the service and its details exist
        if (!service || !service.templateDetails || !service.templateDetails.ConfigService_Typ) {
            console.warn(`Skipping service ID ${change.serviceId}: Missing details or ConfigService_Typ.`);
            return; // Skip this service if essential info is missing
        }

        const configCode = service.templateDetails.ConfigService_Typ.toLowerCase();

        // Combine changed client info and configurations for this service
        const combinedChanges = [
            ...change.changedClientInfo,
            ...change.changedConfigurations
        ];

        // Only add if there are actual changes for this service
        if (combinedChanges.length > 0) {
            if (!formattedData[configCode]) {
                formattedData[configCode] = [];
            }
            // Add the changes to the appropriate group
            formattedData[configCode].push(...combinedChanges);
        }
    });

    return formattedData;
};


  // Function to apply successful changes to the local state
  const applyChangesToState = (savedChanges: ServiceChanges[]) => {
    setServiceConfigurations(currentConfigs => {
      return currentConfigs.map(config => {
        const changeData = savedChanges.find(c => c.serviceId === config.id);
        if (!changeData) {
          return config; // No changes for this config
        }

        // Apply clientInfo changes
        const updatedClientInfo = [...config.clientInfo]; // Start with original
        changeData.changedClientInfo.forEach(change => {
          const existingIndex = updatedClientInfo.findIndex(item => item.key === change.key);
          if (change.flag === 'delete') {
            // Remove deleted item
            if (existingIndex !== -1) {
              updatedClientInfo.splice(existingIndex, 1);
            }
          } else if (existingIndex !== -1) {
            // Update existing item
            updatedClientInfo[existingIndex] = { ...change }; // Use the saved change (without flag)
          } else {
            // Add new item
            updatedClientInfo.push({ ...change }); // Add the saved change (without flag)
          }
        });

        // Apply configurations changes
        const updatedConfigurations = [...config.configurations]; // Start with original
        changeData.changedConfigurations.forEach(change => {
            const existingIndex = updatedConfigurations.findIndex(item => item.key === change.key);
             if (change.flag === 'delete') {
                 // Remove deleted item
                 if (existingIndex !== -1) {
                     updatedConfigurations.splice(existingIndex, 1);
                 }
             } else if (existingIndex !== -1) {
                 // Update existing item
                 updatedConfigurations[existingIndex] = { ...change }; // Use the saved change (without flag)
             } else {
                // Add new item
                 updatedConfigurations.push({ ...change }); // Add the saved change (without flag)
             }
        });


        return {
          ...config,
          clientInfo: updatedClientInfo,
          configurations: updatedConfigurations
        };
      });
    });
  };

 // Function to save template changes (now accepts difference data)
 const handleSaveTemplateChanges = async (changesToSave: ServiceChanges[], tenantId: string, envId: string) => {
    try {
        // 1. Format only the differences for the API
        const formattedData = formatChangesForApi(changesToSave);

        // Check if there's anything to save
        if (Object.keys(formattedData).length === 0) {
             console.log("No changes detected to save.");
             // Optionally return a specific success state or message
             return { success: true, message: "No changes detected." };
        }
        const body = {
          clientInfo: {
            tenantId: '',
            envId: ''
          },
          ...formattedData
        }
        console.log(body)
        // 2. Send the formatted data to the API
        const response = await saveTemplateChanges('123', body);

        // 3. If API save is successful, update local state
        if (response.success) {
            applyChangesToState(changesToSave); // Apply the same changes locally
            return response; // Return API response
        } else {
            throw new Error(response.message || "Failed to save changes via API");
        }
    } catch (error) {
        console.error("Error in handleSaveTemplateChanges:", error);
        // Re-throw the error so the calling component (ServiceConfigurationView) can handle it (e.g., show notification)
        throw error;
    }
 };


  return {
    serviceConfigurations,
    isLoading,
    error,
    saveTemplateChanges: handleSaveTemplateChanges, // Expose the save handler
    findDifferences // Expose helper if needed elsewhere, or keep internal
  };
};